/**
* ─────────────────────────────────────────────────────────────────────
* FILE: src/pages/Library.tsx
* DESCRIPTION: Video and image identity library management interface.
*              Provides browsing, filtering, and management of verified
*              media assets with AI detection flags and tool metadata.
*
* FEATURES:
* - Grid and list view toggle
* - Advanced filtering (content type, AI origin, date, watermark)
* - Search by Persistent ID, filename, or fingerprint (FIXED)
* - Bulk selection and actions
* - Quick preview slide-out panel
* - Sort options (newest, oldest, AI score, risk score)
* - Column sorting in List view (NEW)
* - Thumbnail previews with play buttons for video
* - Fixed menu flickering issue
* - Scrollable table view
* - Proper navigation to identities page
* - Live API integration with backend identity endpoints
*
* BACKEND INTEGRATION (Swagger v2.0.0):
* - GET /v1/identities - List identities with filters/pagination
* - GET /storage/identity/{id} - Get single identity details
* - POST /v1/identities/{id}/reanalyze - Submit for re-analysis
* - GET /storage/identity/{id}/download - Download identity file
*
* STORAGE STRATEGY:
* - Dev: JSON file storage (data/identities.json)
* - Prod: PostgreSQL via Supabase (configurable via STORAGE_BACKEND env)
*
* @copyright 2026-2026 Anou. All rights reserved.
* @author Anou
* @version 2.3.0
* @created 2026-01-15
* @updated 2026-09-05 - Phase 2.5: Scope-aware dashboard — filters identities
*                       and stats by active workspace org_id via useOrgFilter.
*                       Enables data segregation across Personal / Org / Global.
* ─────────────────────────────────────────────────────────────────────
*/
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Grid3X3,
  List,
  MoreVertical,
  Download,
  Shield,
  Eye,
  Trash2,
  Copy,
  Check,
  Filter,
  ChevronDown,
  Play,
  X,
  CheckSquare,
  Square,
  Tag,
  FileImage,
  FileVideo,
  RefreshCw,
  Cpu,
  Bot,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { getApiClient } from '../lib/api/apiClient';
import { ENDPOINTS }    from '../lib/api/endpoints';
import type { VideoIdentity, FileType } from '../lib/api/types';
import toast from 'react-hot-toast';
// Phase 2.4: Scope-aware data segregation
import { useOrgFilter } from '../hooks/useOrgFilter';
import { useWorkspace } from '../contexts/WorkspaceContext';

// ─────────────────────────────────────────────────────────────────────
// TYPES (Mapped from Swagger VideoIdentity to UI-friendly format)
// ─────────────────────────────────────────────────────────────────────
interface LibraryItem {
  id: string;
  filename: string;
  persistentId: string;
  type: 'video' | 'image' | 'audio';
  aiStatus: 'ai-generated' | 'human-created' | 'unclear'; // NEW
  isAiGenerated: boolean; // Keep for sorting
  aiDetectionScore?: number;
  softwareTool?: string;
  status: 'active' | 'flagged' | 'removed' | 'under_review';
  watermarkType: 'visible' | 'invisible' | 'none';
  date: string; // created_at
  thumbnail?: string; // storage_url or undefined (no placeholder)
  aspectRatio?: '16:9' | '1:1' | '4:3' | 'unknown';
  size?: string; // From metadata.file_size
}

interface FilterState {
  contentType: string; // video|image|audio
  aiOrigin: string; // ai-generated|human-created|unclear (NEW)
  dateRange: string; // today|week|month|year
  watermarkType: string; // visible|invisible|none
}

interface ListResponse {
  identities: VideoIdentity[];
  total: number;
  page: number;
  limit: number;
}

// NEW: Sort configuration for List view
type SortField = 'filename' | 'persistentId' | 'type' | 'isAiGenerated' | 'watermarkType' | 'date';
type SortOrder = 'asc' | 'desc';

// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────
const mapIdentityToLibraryItem = (identity: VideoIdentity): LibraryItem => {
  // Backend returns absolute thumbnail URL via BACKEND_PUBLIC_URL.
  // Use as-is — no frontend URL construction needed.
  // Fall back to storage_url only if thumbnail_url is missing.
  const thumbnail = (identity as any).thumbnail_url || identity.storage_url || undefined;

  // Extract aspect ratio from metadata if available
  const aspectRatio = identity.fingerprint?.dimensions
    ? (identity.fingerprint.dimensions.includes('16:9') ? '16:9' :
       identity.fingerprint.dimensions.includes('1:1') ? '1:1' :
       identity.fingerprint.dimensions.includes('4:3') ? '4:3' : 'unknown') // FIXED typo: 'unknonwn' -> 'unknown'
    : undefined;

  // Map watermark type
  const watermarkType = identity.watermark
    ? (identity.watermark.visible ? 'visible' : 'invisible')
    : 'none';

  // Format file size from bytes to human-readable
  const fileSize = (identity.fingerprint as any)?.file_size
    ? `${Math.round((identity.fingerprint as any).file_size / (1024 * 1024))}MB`
    : undefined;

  // ─────────────────────────────────────────────────────────────────
  // AI DETECTION: Use fallback chain like Dashboard (REFERENCE PATTERN)
  // ─────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────
  // AI DETECTION: Updated to match backend response schema
  // ─────────────────────────────────────────────────────────────────
    // ─────────────────────────────────────────────────────────────────
  // AI DETECTION: Backend-driven (Zero frontend heuristics)
  // ─────────────────────────────────────────────────────────────────
  // The backend is the single source of truth. We do NOT calculate 
  // thresholds or guess the origin here.
  const origin = identity.origin;
  const vmie = identity.vmie_detection;

  // 1. AI Status: Read the explicit boolean flag from the backend
  const isAiGeneratedFlag = origin?.is_ai_generated ?? origin?.source_detection?.ai_generated;
  
  let aiStatus: LibraryItem['aiStatus'] = 'human-created';
  if (isAiGeneratedFlag === true) {
    aiStatus = 'ai-generated';
  } else if (isAiGeneratedFlag === undefined) {
    // Backend hasn't assessed it yet (e.g., offline mode, pending AI queue)
    aiStatus = 'unclear';
  }

  // 2. AI Score: Read the exact confidence score provided by the backend
  const rawConfidence = origin?.source_detection?.ai_confidence ?? vmie?.total_score;
  const aiDetectionScore = rawConfidence !== undefined && rawConfidence > 0 ? rawConfidence : undefined;

  // 3. Software Tool: Read directly from backend markers
  const softwareTool = vmie?.matches?.[0]?.identifier || origin?.source_detection?.ai_detector || undefined;

  return {
    id: identity.persistent_id,
    filename: identity.original_filename || 'Unknown',
    persistentId: identity.persistent_id,
    type: identity.file_type as 'video' | 'image' | 'audio',
    isAiGenerated: aiStatus === 'ai-generated', // Strict mapping
    aiStatus,
    aiDetectionScore: aiDetectionScore && aiDetectionScore > 0 ? aiDetectionScore : undefined,
    softwareTool,
    status: identity.status,
    watermarkType,
    date: identity.created_at,
    thumbnail,
    aspectRatio,
    size: fileSize,
  };
};

// Color helpers for AI origin flag (Dashboard-style: blue=AI, emerald=human)
const getAiOriginColor = (status: string) => {
  switch (status) {
    case 'ai-generated': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800';
    case 'unclear': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800';
    default: return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800';
  }
};
const getAiOriginLabel = (status: string) => {
  switch (status) {
    case 'ai-generated': return 'AI Generated';
    case 'unclear': return 'Unclear';
    default: return 'Human Created';
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'active': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300';
    case 'flagged': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
    case 'removed': return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getWatermarkLabel = (type: string) => {
  switch (type) {
    case 'visible': return 'Visible';
    case 'invisible': return 'Invisible';
    default: return 'None';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Format bytes to human-readable
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const Library: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewPanel, setPreviewPanel] = useState<LibraryItem | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const menuTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  
  // NEW: List view column sorting state
  const [listSortField, setListSortField] = useState<SortField>('date');
  const [listSortOrder, setListSortOrder] = useState<SortOrder>('desc');

  // Scope-aware data segregation
  const orgFilter = useOrgFilter();
  const { scope } = useWorkspace();
  
  const [filters, setFilters] = useState<FilterState>({
    contentType: '',
    aiOrigin: '', // REPLACED: was trustScoreRange/status
    dateRange: '',
    watermarkType: '',
  });

  // API state
  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  //const itemsPerPage = 12;
  const itemsPerPage = 50;

  // ───────────────────────────────────────────────────────────────────
  // LOAD DATA FROM BACKEND
  // ───────────────────────────────────────────────────────────────────
  const loadLibraryData = useCallback(async (page: number = 1) => {
    try {
      setIsLoading(true);
      const apiClient = getApiClient();

      // Build API query params from UI filters
      const apiParams: Record<string, any> = {
        page,
        limit: itemsPerPage,
      };

      // Phase 2.4: Scope-aware filter - only show identities from active workspace
      if (orgFilter) {
        apiParams.org_id = orgFilter;
      }

      if (filters.contentType) {
        apiParams.file_type = filters.contentType;
      }
      // NEW: AI Origin filter mapping
      // AI Origin filter mapping (Pushed to Supabase via backend)
      if (filters.aiOrigin) {
        if (filters.aiOrigin === 'ai-generated') {
          apiParams.origin_is_ai_generated = true;
        } else if (filters.aiOrigin === 'human-created') {
          apiParams.origin_is_ai_generated = false;
        }
        // 'unclear' is handled client-side since backend doesn't filter by null
      }

      if (filters.dateRange) {
        const now = new Date();
        let fromDate: Date;
        switch (filters.dateRange) {
          case 'today': fromDate = new Date(now.setHours(0,0,0,0)); break;
          case 'week': fromDate = new Date(now.setDate(now.getDate() - 7)); break;
          case 'month': fromDate = new Date(now.setMonth(now.getMonth() - 1)); break;
          case 'year': fromDate = new Date(now.setFullYear(now.getFullYear() - 1)); break;
          default: fromDate = new Date(0);
        }
        apiParams.date_from = fromDate.toISOString();
      }
      // FIXED: Search functionality - ensure query is properly encoded and passed
      if (searchQuery && searchQuery.trim()) {
        apiParams.search = searchQuery.trim();
      }

      // Fetch from backend - GET /v1/identities
      const response = await apiClient.get<ListResponse>(
        ENDPOINTS.IDENTITIES_LIST,
        apiParams
      );

      // Map backend VideoIdentity to UI LibraryItem
      const mappedItems = (response.identities || []).map(mapIdentityToLibraryItem);
      
      // Client-side filter for 'unclear' AI origin (if backend doesn't support null)
      const filteredItems = filters.aiOrigin === 'unclear'
        ? mappedItems.filter(item => item.aiDetectionScore === undefined || item.aiDetectionScore === null)
        : mappedItems;
      
      setLibraryItems(filteredItems);
      setTotalItems(response.total); // Note: total may not reflect client-side 'unclear' filter
    } catch (error: any) {
      console.error('Failed to load library ', error);
      toast.error('Failed to load identities');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [filters, searchQuery, orgFilter]);

  // ───────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadLibraryData(currentPage);
    }, [loadLibraryData, currentPage, orgFilter]);

  // Cleanup menu timeout on unmount
  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    };
  }, []);

  // ───────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────
  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === libraryItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(libraryItems.map(i => i.id)));
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Copied to clipboard');
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      contentType: '',
      aiOrigin: '',
      dateRange: '',
      watermarkType: '',
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadLibraryData(currentPage);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this identity? This action cannot be undone.')) {
      return;
    }
    try {
      const apiClient = getApiClient();
      await apiClient.delete(ENDPOINTS.IDENTITY_DETAIL(id));
      setLibraryItems(prev => prev.filter(item => item.id !== id));
      setTotalItems(prev => prev - 1);
      toast.success('Identity deleted');
    } catch (error: any) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete identity');
    }
  };

  // NEW: List view column sort handler
  const handleListSort = (field: SortField) => {
    if (listSortField === field) {
      // Toggle order if same field
      setListSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to desc
      setListSortField(field);
      setListSortOrder('desc');
    }
  };

  // NEW: Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (listSortField !== field) return null;
    return listSortOrder === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  // Fixed menu handling with debounce to prevent flickering
  const handleMenuToggle = (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    
    if (activeMenuId === itemId) {
      setActiveMenuId(null);
    } else {
      menuTimeoutRef.current = setTimeout(() => {
        setActiveMenuId(itemId);
      }, 50);
    }
  };

  const handleMenuClose = () => {
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    setActiveMenuId(null);
  };

  // ───────────────────────────────────────────────────────────────────
  // FILTERED & SORTED DATA
  // ───────────────────────────────────────────────────────────────────
  
  // Client-side sort for List view columns
  const getSortedItems = (items: LibraryItem[]) => {
    if (viewMode !== 'list') return items;
    
    return [...items].sort((a, b) => {
      let comparison = 0;
      
      switch (listSortField) {
        case 'filename':
          comparison = a.filename.localeCompare(b.filename);
          break;
        case 'persistentId':
          comparison = a.persistentId.localeCompare(b.persistentId);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'isAiGenerated':
          // Sort AI-generated first, then human
          comparison = a.isAiGenerated === b.isAiGenerated ? 0 : a.isAiGenerated ? -1 : 1;
          break;
        case 'watermarkType':
          comparison = a.watermarkType.localeCompare(b.watermarkType);
          break;
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return listSortOrder === 'asc' ? comparison : -comparison;
    });
  };

  const sortedItems = getSortedItems([...libraryItems].sort((a, b) => {
    // Grid view or global sort (newest/oldest/etc)
    if (viewMode === 'grid') {
      switch (sortBy) {
        case 'newest':
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case 'oldest':
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'trust-high':
          return (b.aiDetectionScore || 0) - (a.aiDetectionScore || 0);
        case 'trust-low':
          return (a.aiDetectionScore || 0) - (b.aiDetectionScore || 0);
        default:
          return 0;
      }
    }
    return 0;
  }));

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // ───────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────
  if (isLoading && libraryItems.length === 0) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-80 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[80px] px-4 sm:px-6 lg:px-8 pb-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Page Title & Actions */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Identities
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              {totalItems} verified media assets
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filter Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-4">
            {/* Search Input - Full width on mobile */}
            <div className="relative w-full sm:flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    loadLibraryData(1);
                  }
                }}
                placeholder="Search by Persistent ID, filename, or fingerprint"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {/* Sort By + View Toggle Row - Full width on mobile, inline on desktop */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Sort by</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 sm:flex-initial px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 min-w-0"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="trust-high">AI Score (High-Low)</option>
                  <option value="trust-low">AI Score (Low-High)</option>
                </select>
              </div>
              {/* View Toggle - Fixed width, aligned height */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex-shrink-0">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white dark:bg-gray-600 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-gray-600 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Filter Tags - UPDATED: AI Origin filter replaces Status */}
                      {/* Filter Tags - Hidden on mobile, visible on sm+ */}
            <div className="hidden sm:flex flex-wrap items-center gap-3">
              <select
                value={filters.contentType}
                onChange={(e) => handleFilterChange('contentType', e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Content Type</option>
                <option value="video">Video</option>
                <option value="image">Image</option>
                <option value="audio">Audio</option>
              </select>

              {/* REPLACED: Trust Score Range with AI Origin filter */}
              <select
                value={filters.aiOrigin}
                onChange={(e) => handleFilterChange('aiOrigin', e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">AI Origin</option>
                <option value="ai-generated">AI-Generated</option>
                <option value="human-created">Human-Created</option>
                <option value="unclear">Unclear</option>
              </select>

              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Date Range</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>

              {/* REMOVED: Status filter (was Active/Flagged/etc) */}
              <select
                value={filters.watermarkType}
                onChange={(e) => handleFilterChange('watermarkType', e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 border-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Watermark Type</option>
                <option value="visible">Visible</option>
                <option value="invisible">Invisible</option>
                <option value="none">None</option>
              </select>

              <div className="flex-1" />

              {(searchQuery || Object.values(filters).some(f => f)) && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Clear All
                </button>
              )}
                  
            </div>            
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                {selectedIds.size} selected
              </span>
              <button
                onClick={selectAll}
                className="text-sm text-blue-700 dark:text-blue-400 hover:underline"
              >
                Select All
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                <Download className="w-4 h-4" />
                Export Selected
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                <Shield className="w-4 h-4" />
                Verify Watermarks
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium text-white">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Identity Grid */}
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            IDENTITY GRID ({totalItems})
          </h2>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sortedItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate(`/identities/${item.id}`)}
                  className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-gray-100 dark:bg-gray-700">
                    {item.thumbnail ? (
                      <img
                        src={item.thumbnail}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // If backend thumbnail fails, hide image entirely (no placeholder loop)
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      // Empty state: no placeholder, just subtle background
                      <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                        {item.type === 'video' ? <FileVideo className="w-8 h-8" /> : <FileImage className="w-8 h-8" />}
                      </div>
                    )}
                    
                    {item.type === 'video' && item.thumbnail && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <Play className="w-5 h-5 text-gray-900 dark:text-white ml-1" />
                        </div>
                      </div>
                    )}
                    
                    {/* Top-left corner pill: subtle transparent style for future info */}
                    <div className="absolute top-2 left-2 px-2 py-1 bg-black/30 dark:bg-white/20 text-white/90 dark:text-gray-200 text-xs rounded-full backdrop-blur-sm border border-white/20 dark:border-black/20">
                      {item.type.toUpperCase()}
                    </div>
                    
                    {/* 3-Dot Menu - Fixed flickering */}
                    <div className="absolute top-2 right-2">
                      <button
                        onClick={(e) => handleMenuToggle(item.id, e)}
                        onMouseEnter={() => {
                          if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                        }}
                        className="p-2 bg-white/90 dark:bg-gray-800/90 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-900 dark:text-white" />
                      </button>
                      {activeMenuId === item.id && (
                        <div
                          onMouseEnter={() => {
                            if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                          }}
                          onMouseLeave={handleMenuClose}
                          className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/identities/${item.id}`);
                              handleMenuClose();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white rounded-t-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            View Full Analysis
                          </button>
                          <button
                            disabled
                            onClick={(e) => {
                              e.stopPropagation();
                              toast('Download started');
                              handleMenuClose();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                          <button
                            disabled
                            onClick={(e) => {
                              e.stopPropagation();
                              toast('Verifying watermark...');
                              handleMenuClose();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
                          >
                            <Shield className="w-4 h-4" />
                            Verify Watermark
                          </button>
                          <button
                            disabled
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id);
                              handleMenuClose();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate mb-1">
                      {item.filename}
                    </h3>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                        {item.persistentId}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(item.persistentId, item.id);
                        }}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        {copiedId === item.id ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3 text-gray-400" />
                        )}
                      </button>
                    </div>
                    
                    {/* UPDATED: AI Origin pill + AI score (Dashboard-style colors) */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getAiOriginColor(item.aiStatus)}`}>
                        <Bot className="w-3 h-3" />
                        {getAiOriginLabel(item.aiStatus)}
                      </span>
                      {item.aiDetectionScore !== undefined && item.aiDetectionScore > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                          {(item.aiDetectionScore * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                    
                    {/* Software Tool pill */}
                    <div className="flex items-center gap-2 mb-3">
                      {item.softwareTool && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                          <Cpu className="w-3 h-3" />
                          {item.softwareTool}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{getWatermarkLabel(item.watermarkType)}</span>
                      <span>{formatDate(item.date)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* List View - With column sorting */
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <button onClick={selectAll} className="hover:opacity-70">
                          {selectedIds.size === sortedItems.length ? (
                            <CheckSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          )}
                        </button>
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Thumbnail</th>
                      {/* Sortable column headers */}
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => handleListSort('filename')}
                      >
                        <div className="flex items-center">
                          Filename
                          {getSortIcon('filename')}
                        </div>
                      </th>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => handleListSort('persistentId')}
                      >
                        <div className="flex items-center">
                          Persistent ID
                          {getSortIcon('persistentId')}
                        </div>
                      </th>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => handleListSort('type')}
                      >
                        <div className="flex items-center">
                          Type
                          {getSortIcon('type')}
                        </div>
                      </th>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => handleListSort('isAiGenerated')}
                      >
                        <div className="flex items-center">
                          AI Origin
                          {getSortIcon('isAiGenerated')}
                        </div>
                      </th>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => handleListSort('watermarkType')}
                      >
                        <div className="flex items-center">
                          Watermark
                          {getSortIcon('watermarkType')}
                        </div>
                      </th>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => handleListSort('date')}
                      >
                        <div className="flex items-center">
                          Created
                          {getSortIcon('date')}
                        </div>
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedItems.map((item) => (
                      <tr 
                        key={item.id} 
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${
                          selectedIds.has(item.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                        onClick={() => toggleSelection(item.id)}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => toggleSelection(item.id)} className="hover:opacity-70">
                            {selectedIds.has(item.id) ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="w-16 h-12 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                            {item.thumbnail ? (
                              <img 
                                src={item.thumbnail} 
                                alt="" 
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                                {item.type === 'video' ? <FileVideo className="w-4 h-4" /> : <FileImage className="w-4 h-4" />}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.filename}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">{item.persistentId}</td>
                        <td className="px-4 py-3">
                          {item.type === 'video' ? (
                            <FileVideo className="w-4 h-4 text-purple-600" />
                          ) : item.type === 'image' ? (
                            <FileImage className="w-4 h-4 text-blue-600" />
                          ) : (
                            <FileImage className="w-4 h-4 text-gray-600" />
                          )}
                        </td>
                        {/* AI Origin pill in list - Dashboard-style colors */}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getAiOriginColor(item.aiStatus)}`}>
                            <Bot className="w-3 h-3" />
                            {getAiOriginLabel(item.aiStatus)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{getWatermarkLabel(item.watermarkType)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDate(item.date)}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuToggle(item.id, e);
                            }}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          </button>
                          {activeMenuId === item.id && (
                            <div
                              onMouseEnter={() => {
                                if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                              }}
                              onMouseLeave={handleMenuClose}
                              className="absolute right-4 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/identities/${item.id}#forensics`);
                                  handleMenuClose();
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white rounded-t-lg transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                View Forensics
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toast('Submitted for re-analysis');
                                  handleMenuClose();
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
                              >
                                <Cpu className="w-4 h-4" />
                                Re-analyze (VMIE)
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toast('Download started');
                                  handleMenuClose();
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
                              >
                                <Download className="w-4 h-4" />
                                Download
                              </button>
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/identities/${item.id}`);
                                  handleMenuClose();
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
                              >
                                <Shield className="w-4 h-4" />
                                Verify Watermark
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(item.id);
                                  handleMenuClose();
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = currentPage <= 3
                      ? i + 1
                      : currentPage >= totalPages - 2
                      ? totalPages - 4 + i
                      : currentPage - 2 + i;
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {libraryItems.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <FileVideo className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No identities found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchQuery || Object.values(filters).some(f => f)
                ? 'Try adjusting your filters or search query'
                : 'Upload files to get started'}
            </p>
            {!searchQuery && !Object.values(filters).some(f => f) && (
              <button
                onClick={() => navigate('/analyze')}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Upload Files
              </button>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close menus */}
      {activeMenuId && (
        <div
          className="fixed inset-0 z-40"
          onClick={handleMenuClose}
        />
      )}
    </div>
  );
};

export default Library;
